import { useEventBus } from 'src/hooks';
import { ON_CREATE_CONSOLIDATIONS, ON_DELETE_CONSOLIDATIONS, ON_EDIT_CONSOLIDATIONS } from 'src/hooks/EventBus/eventTypes';
import { consolidationService } from 'src/services/consolidation';
import { withFulFilled, withPending, withRejected } from '../reducers/commons';

export const PROCESSING_CONSOLIDATIONS = 'PROCESSING_CONSOLIDATIONS';
export const CLEAR_CONSOLIDATION_STATUS = 'CLEAR_CONSOLIDATION_STATUS';

const eventBus = useEventBus();

/**
 * resolver: objeto que tem o objetivo de fazer o handle das requisições
 * de configurações feitas pelo usuário. Basicamente, como um usuário
 * pode fazer diversas modificações ao mesmo tempo (POST, PUT e DELETE),
 * e gerar mais de uma requisição ao mesmo tempo, nós não atualizamos
 * o estado do redux para cada mudança do estado da promise da requisição,
 * e sim quando todas as promises estão resolvidas, com erro ou não.
 */
const resultPromiseResolver = {
  // Promises das requisições
  promises: [],
  // Dispatch: redux
  dispatch: null,
  // Numero total de requisições que serão feitas
  actionsLength: 0,
  // setter
  setActionsLength(quantity) {
    this.actionsLength = quantity;
  },
  // adiciona e dispara uma callback quando todas as promises estiverem
  // alocadas em this.promises
  pushPromise(promise) {
    this.promises.push(promise);

    if (this.promises.length === this.actionsLength) {
      this.dispatchOnAllPromises();
    }
  },
  // setter
  setDispatch(dispatch) {
    this.dispatch = dispatch;
  },
  /**
   * Realiza todo o handling do estado das promises, fazendo o "dispatch" desse
   * state para posterior atualização do estado global relacionado à consolidações
   */
  async dispatchOnAllPromises() {
    // pending
    this.dispatch({ type: withPending(PROCESSING_CONSOLIDATIONS) });

    // Aqui esperamos todas as promises serem resolvidas, temos o .catch
    // para obter uma referência para as promises rejeitadas.
    const results = await Promise.all(
      this.promises.map((promise) => promise.catch((error) => error))
    );

    const errors = [];

    const success = results.filter((result) => {
      if (result instanceof Error) {
        errors.push(result);
        return false;
      }
      return true;
    });

    if (!!success.length) {
      this.dispatch({
        type: withFulFilled(PROCESSING_CONSOLIDATIONS),
        payload: { status: 1 },
      });
    }

    if (!!errors.length) {
      this.dispatch({
        type: withRejected(PROCESSING_CONSOLIDATIONS),
        payload: { error: errors, ...(!success.length ? { status: 0 } : {}) },
      });
    }

    // Reseta os dados de checagem
    this.reset();
  },
  reset() {
    this.dispatch({
      type: CLEAR_CONSOLIDATION_STATUS,
    });

    this.actionsLength = 0;
    this.promises.length = 0;
    this.dispatch = null;
  },
};

/**
 * intermediador da ação de POST de uma consolidação: faz a requisição
 * e retorna a promise.
 */
function createConsolidations(consolidations, clientId, surveyId, analyzeId) {
  const resultPromise = consolidationService.createConsolidations(
    clientId,
    surveyId,
    analyzeId,
    consolidations
  );

  resultPromise.then(() => {
    eventBus.notify(
      ON_CREATE_CONSOLIDATIONS,
      consolidations
    );
  });

  return resultPromise;
}

/**
 * intermediador da ação de PUT de uma consolidação: faz a requisição
 * e retorna a promise.
 */
function updateConsolidations(consolidations, consolidationsWithTitleChanged, clientId, surveyId, analyzeId) {
  const resultPromise = consolidationService.updateConsolidations(
    clientId,
    surveyId,
    analyzeId,
    consolidations
  );

  resultPromise.then(() => {
    eventBus.notify(
      ON_EDIT_CONSOLIDATIONS,
      consolidationsWithTitleChanged,
    );
  });

  return resultPromise;
}

/**
 * intermediador da ação de DELETE de uma consolidação: faz a requisição
 * e retorna a promise.
 */
function deleteConsolidations(consolidations, clientId, surveyId, analyzeId) {
  const identifiers = consolidations.map((cons) => cons.value).join(',');
  const resultPromise = consolidationService.deleteConsolidations(
    clientId,
    surveyId,
    analyzeId,
    identifiers
  );

  resultPromise.then(() => {
    eventBus.notify(
      ON_DELETE_CONSOLIDATIONS,
      consolidations
    );
  });

  return resultPromise;
}

// Ordem em que as ações devem ser executadas
const actions = [
  deleteConsolidations,
  updateConsolidations,
  createConsolidations,
];

export function processConsolidationActions(
  consolidationConfig,
  clientId,
  surveyId,
  analyzeId,
  dispatch
) {
  // Retorna as consolidações que devem ser criadas (creates -> post)
  // atualizadas (updates -> put) e deletedas (deletes -> delete)
  const { creates, updates, deletes, updatesByTitle } = consolidationConfig;

  const data = [deletes, updates, creates];

  // Numero de requisições que serão feitas
  const numOfActions =
    Number(!!creates.length) +
    Number(!!updates.length) +
    Number(!!deletes.length);

  if (!!numOfActions) {
    resultPromiseResolver.setDispatch(dispatch);
    resultPromiseResolver.setActionsLength(numOfActions);

    // Iteramos as ações (pense em ações como POST, PUT e DELETE) que serão
    // executadas de acordo com os dados de configuração.
    actions.forEach((action, index) => {
      if (!!data[index].length) {
        const args =
          index !== 1
            ? [data[index], clientId, surveyId, analyzeId]
            : [data[index], updatesByTitle, clientId, surveyId, analyzeId];
        const resultPromise = action(...args);
        // Chamamos o pushPromise que terá todo o trabalho interno de handling
        // das promises.
        resultPromiseResolver.pushPromise(resultPromise);
      }
    });
  }
}
